import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/supplychain`;

export default function SupplyChain() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState<'production' | 'forecasts' | 'quality' | 'bom'>('production');
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [prodForm, setProdForm] = useState({ order_number: '', item_code: '', planned_quantity: '', planned_start: '', planned_end: '' });
  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await authFetch(withEntityParam(`${API}/summary`, entity)); if (sRes.ok) setSummary(await sRes.json());
      await loadTab(tab);
    } catch (e) { console.error(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  const loadTab = async (t: string) => {
    setTab(t as any);
    const urls: Record<string, string> = { production: `${API}/production`, forecasts: `${API}/forecasts`, quality: `${API}/quality`, bom: `${API}/bom/ALL` };
    const res = await authFetch(withEntityParam(urls[t] || urls.production, entity));
    if (res.ok) { const d = await res.json(); setData(d.orders || d.forecasts || d.inspections || d.components || []); }
  };

  const handleCreate = async () => {
    const payload = { ...prodForm, planned_quantity: parseFloat(prodForm.planned_quantity) || 0 };
    const res = await authFetch(`${API}/production`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { showToast('Production order created', true); setShowCreate(false); loadTab('production'); load(); } else { const e = await res.json(); showToast(e.detail || 'Error', false); }
  };

  const handleAction = async (pk: number, action: string, version: number) => {
    const body = action === 'complete' ? { expected_version: version, completed_qty: 0, material_cost: 0, labor_cost: 0, overhead_cost: 0 } : { expected_version: version };
    const res = await authFetch(`${API}/production/${pk}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (res.ok) { showToast(`Order ${action}d`, true); loadTab('production'); } else { const e = await res.json(); showToast(e.detail || 'Failed', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const productionColumns = [
    { key: 'order_number', label: 'Order #', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'item_code', label: 'Item', render: (v: any) => v || '-' },
    { key: 'planned_quantity', label: 'Planned Qty', align: 'right' as const },
    { key: 'completed_quantity', label: 'Completed', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'planned_start', label: 'Start', render: (v: any) => v || '-' },
    { key: 'planned_end', label: 'End', render: (v: any) => v || '-' },
    { key: 'total_cost', label: 'Cost', align: 'right' as const, render: (v: any) => formatAmount(v) },
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_: any, o: any) => (
      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
        {o.lifecycle_status === 'PLANNED' && <ActionButton label="Release" onClick={(e) => { e.stopPropagation(); handleAction(o.id, 'release', o.version); }} variant="secondary" size="sm" />}
        {o.lifecycle_status === 'RELEASED' && <ActionButton label="Start" onClick={(e) => { e.stopPropagation(); handleAction(o.id, 'start', o.version); }} variant="primary" size="sm" />}
        {o.lifecycle_status === 'IN_PROGRESS' && <ActionButton label="Complete" onClick={(e) => { e.stopPropagation(); handleAction(o.id, 'complete', o.version); }} variant="success" size="sm" />}
      </div>
    )},
  ];

  const forecastColumns = [
    { key: 'item_code', label: 'Item', render: (v: any) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'period', label: 'Period' },
    { key: 'forecast_quantity', label: 'Forecast', align: 'right' as const },
    { key: 'actual_quantity', label: 'Actual', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'forecast_method', label: 'Method' },
    { key: 'confidence', label: 'Confidence', align: 'right' as const, render: (v: any) => v != null ? `${v}%` : '-' },
  ];

  const qualityColumns = [
    { key: 'inspection_type', label: 'Type', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'item_code', label: 'Item', render: (v: any) => v || '-' },
    { key: 'sample_size', label: 'Sample', align: 'right' as const },
    { key: 'defects_found', label: 'Defects', align: 'right' as const, render: (v: any) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--positive)' }}>{v}</span> },
    { key: 'defect_rate', label: 'Rate', align: 'right' as const, render: (v: any) => `${v}%` },
    { key: 'disposition', label: 'Disposition', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'inspection_date', label: 'Date', render: (v: any) => v || '-' },
  ];

  const currentColumns = tab === 'production' ? productionColumns : tab === 'forecasts' ? forecastColumns : qualityColumns;

  return (
    <PageLayout
      title="Supply Chain"
      subtitle="Demand planning, production orders, BOM, and quality management"
      action={tab === 'production' ? { label: '+ Production Order', onClick: () => setShowCreate(true) } : undefined}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Forecasts', value: summary.forecasts },
          { label: 'Prod Orders', value: summary.production_orders },
          { label: 'In Progress', value: summary.in_progress, color: 'var(--warning)' },
          { label: 'BOM Items', value: summary.bom_items },
        ]} />
      )}

      <TabBar
        tabs={[
          { key: 'production', label: 'Production' },
          { key: 'forecasts', label: 'Forecasts' },
          { key: 'quality', label: 'Quality' },
        ]}
        active={tab}
        onChange={(t) => loadTab(t)}
      />

      <DataTable
        columns={currentColumns}
        rows={data}
        loading={loading}
        emptyMessage={`No ${tab} data.`}
        rowKey="id"
      />

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Production Order" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><div style={{ display: 'flex', gap: 8 }}><input placeholder="Order Number *" value={prodForm.order_number} onChange={e => setProdForm({ ...prodForm, order_number: e.target.value })} style={inputStyle} /><input placeholder="Item Code" value={prodForm.item_code} onChange={e => setProdForm({ ...prodForm, item_code: e.target.value })} style={inputStyle} /></div><input placeholder="Planned Quantity" type="number" value={prodForm.planned_quantity} onChange={e => setProdForm({ ...prodForm, planned_quantity: e.target.value })} style={inputStyle} /><div style={{ display: 'flex', gap: 8 }}><div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Start</label><input type="date" value={prodForm.planned_start} onChange={e => setProdForm({ ...prodForm, planned_start: e.target.value })} style={inputStyle} /></div><div style={{ flex: 1 }}><label style={{ fontSize: 10, color: 'var(--text-secondary)' }}>End</label><input type="date" value={prodForm.planned_end} onChange={e => setProdForm({ ...prodForm, planned_end: e.target.value })} style={inputStyle} /></div></div></div>
      </Modal>
      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
